import { matchPath, useLocation, useParams } from 'react-router-dom';

import { AppRouteKey, PlatformRoleEnum, PersonaRoleEnum } from './route_types';
import { AppRoutes } from './routes';

export type AppRouteKeys = keyof typeof AppRoutes;

// Get route path with optional params for dynamic routes
export const getRoutePath = (route: AppRouteKey, params?: Record<string, string>): string => {
  const basePath = AppRoutes[route].path;
  if (!params) return basePath;

  // Simple placeholder replacement for dynamic routes
  let path = basePath;
  Object.entries(params).forEach(([key, value]) => {
    path = path.replace(`:${key}`, value);
  });

  return path;
};

export const getRouteKeyFromPath = (path: string): AppRouteKey => {
  // Check for exact path matches
  for (const [key, route] of Object.entries(AppRoutes)) {
    if (route.path === path) {
      return key as AppRouteKey;
    }
  }

  // Handle dynamic routes
  for (const [key, route] of Object.entries(AppRoutes)) {
    if (route.path.includes(':')) {
      const pathPattern = route.path.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pathPattern}$`);

      if (regex.test(path)) {
        return key as AppRouteKey;
      }
    }
  }

  return AppRouteKey.HOME;
};

export const getRouteName = (route: AppRouteKey): string => AppRoutes[route].name;

export const getRouteFromPath = (path: string): AppRouteKey | undefined => {
  for (const [key, info] of Object.entries(AppRoutes)) {
    const routePath = info.path;

    // Handle wildcard route (*)
    if (routePath === '*') {
      return key as AppRouteKey;
    }

    // Convert route path with :params into regex
    const regex = new RegExp(
      '^' +
        routePath
          .replace(/:[^/]+/g, '[^/]+') // replace :param with segment matcher
          .replaceAll('*', '.*') + // handle wildcards
        '$'
    );

    if (regex.test(path)) {
      return key as AppRouteKey;
    }
  }

  return undefined;
};

// Get the parent route key of a route
export const getParentRoute = (route: AppRouteKey): AppRouteKey | undefined => {
  const parentKey = AppRoutes[route].parent;
  return parentKey ? (parentKey as AppRouteKey) : undefined;
};

// Check if a route is a parent of another route
export const isParentOf = (parentRoute: AppRouteKey, childRoute: AppRouteKey): boolean => {
  let current: AppRouteKey | undefined = childRoute;

  while (current) {
    if (current === parentRoute) return true;

    const parentKey = AppRoutes[current].parent;
    if (!parentKey) break;

    current = parentKey as AppRouteKey;
  }

  return false;
};

// Helper for path matching to determine if path belongs to route hierarchy
export const isPathInRouteHierarchy = (basePath: string, pathToCheck: string): boolean => {
  // Remove trailing slashes for consistent comparison
  const normalizedBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
  const normalizedPath = pathToCheck.endsWith('/') ? pathToCheck.slice(0, -1) : pathToCheck;

  // Check if the path is exactly the base or starts with base followed by /
  return normalizedPath === normalizedBase || normalizedPath.startsWith(normalizedBase + '/');
};

// Find all routes that are children of a given route
export const findChildRoutes = (parentRoute: AppRouteKey): AppRouteKey[] => {
  return Object.entries(AppRoutes)
    .filter(([_, info]) => info.parent === parentRoute)
    .map(([key]) => key as AppRouteKey);
};

// Helper function to normalize string to enum
export function normalizePlatformRole(role: string | undefined): PlatformRoleEnum | undefined {
  if (!role) return undefined;
  return PlatformRoleEnum[role.toUpperCase() as keyof typeof PlatformRoleEnum];
}

// Helper function to normalize string to enum
export function normalizePersonaRole(role: string | undefined): PersonaRoleEnum | undefined {
  // replace all spaces with underscores
  const normalizedRole = role?.replace(/\s+/g, '_').toUpperCase();
  if (!normalizedRole) return undefined;
  return PersonaRoleEnum[normalizedRole as keyof typeof PersonaRoleEnum];
}

export interface BreadcrumbItem {
  name: string;
  nameKey?: string;
  path?: string;
  isDynamicName?: boolean;
}

// Season Settings keeps its season + active tab in the query string, but detail pages reached from
// it (version files, operational/harvest versions) have their own URLs. The Season Settings page
// records its last `?seasonId=...&tab=...` here so a breadcrumb can return to the same view
// instead of the empty "Select a Season" screen.
export const SEASON_SETTINGS_LAST_VIEW_KEY = 'season_settings_last_view';

// Same idea for the Production Head Seasons list (`?seasonStatus=...` tab).
export const PRODUCTION_SEASONS_LAST_VIEW_KEY = 'production_seasons_last_view';

const readStoredQuery = (key: string): string => {
  try {
    return (typeof window !== 'undefined' && window.sessionStorage.getItem(key)) || '';
  } catch {
    return '';
  }
};

const readSeasonSettingsLastView = (): string => readStoredQuery(SEASON_SETTINGS_LAST_VIEW_KEY);

export function useBreadcrumbs(loaderData?: any): BreadcrumbItem[] {
  const { pathname, search } = useLocation();
  const params = useParams();

  const breadcrumbParts = Object.values(AppRoutes)
    .filter((route) => matchPath({ path: route.path, end: false }, pathname))
    .map((route) => {
      // Generate the actual path by replacing params
      let actualPath = route.path;
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          actualPath = actualPath.replace(`:${key}`, value).replace(`:${key}?`, value);
        }
      });

      // A prefix-matched ancestor route (e.g. `/season-settings/:seasonVersionId` matching
      // `/season-settings/operational/:id`) can have params the current route doesn't define, so
      // its link keeps a literal `:param` and navigating to it throws. Fall back to the nearest
      // ancestor whose path fully resolves.
      let fallbackKey = route.parent as AppRouteKey | undefined;
      while (/:[A-Za-z]/.test(actualPath) && fallbackKey) {
        actualPath = AppRoutes[fallbackKey].path;
        fallbackKey = AppRoutes[fallbackKey].parent as AppRouteKey | undefined;
      }

      if (actualPath === AppRoutes.PRODUCTION_HEAD_SEASON_SETTINGS.path) {
        actualPath += readSeasonSettingsLastView();
      }

      // Use getName function if available, otherwise use static name
      const hasDynamicName = !!route.getName;
      const dynamicName = hasDynamicName ? route.getName!({ params, loaderData }) : route.name;

      return {
        name: dynamicName,
        nameKey: route.nameKey,
        path: actualPath,
        isDynamicName: hasDynamicName,
      };
    });

  // Production Head season drill-down: Seasons list -> Season Detail -> Distribute Targets. Those
  // pages are separate top-level routes (no shared URL prefix), so prefix matching would only ever
  // produce a single crumb. Build the trail explicitly; each parent crumb keeps its query string
  // (selected status tab / seasonId) so it returns to the same view.
  const isSeasonDetailPage = pathname === AppRoutes.PRODUCTION_HEAD_SEASON_DETAIL.path;
  const isDistributeTargetsPage = pathname === AppRoutes.PRODUCTION_HEAD_DISTRIBUTE_TARGETS.path;
  if (isSeasonDetailPage || isDistributeTargetsPage) {
    const seasonId = new URLSearchParams(search).get('seasonId');
    const seasonsCrumb: BreadcrumbItem = {
      name: AppRoutes.PRODUCTION_HEAD_SEASON.name,
      nameKey: AppRoutes.PRODUCTION_HEAD_SEASON.nameKey,
      path:
        AppRoutes.PRODUCTION_HEAD_SEASON.path + readStoredQuery(PRODUCTION_SEASONS_LAST_VIEW_KEY),
    };
    const detailRoute = AppRoutes.PRODUCTION_HEAD_SEASON_DETAIL;
    const detailPath = seasonId
      ? `${detailRoute.path}?${new URLSearchParams({ seasonId }).toString()}`
      : detailRoute.path;
    const detailCrumb: BreadcrumbItem = {
      name: detailRoute.name,
      nameKey: detailRoute.nameKey,
      path: detailPath,
    };
    if (isSeasonDetailPage) return [seasonsCrumb, detailCrumb];

    const targetsRoute = AppRoutes.PRODUCTION_HEAD_DISTRIBUTE_TARGETS;
    return [
      seasonsCrumb,
      detailCrumb,
      { name: targetsRoute.name, nameKey: targetsRoute.nameKey, path: pathname },
    ];
  }

  // On the Season Config version page, "Season Version Details" is the current page, so it was the
  // last (non-clickable) crumb, unlike the Operational/Harvest version pages whose trail ends in
  // "Version". Give it the same shape: "Season Version Details" returns to the Season Settings view
  // and a trailing "Version" crumb marks the current page.
  // The catch-all `*` route also prefix-matches every path, so it trails the real crumbs
  // ("Not Found" — dropped by the top bar), hence the filter before checking the last crumb.
  const realParts = breadcrumbParts.filter((b) => b.name !== 'Not Found');
  const lastPart = realParts[realParts.length - 1];
  if (
    params.seasonVersionId &&
    lastPart?.nameKey === AppRoutes.PRODUCTION_HEAD_SEASON_SETTINGS_VERSION_DETAILS.nameKey
  ) {
    return [
      ...realParts.slice(0, -1),
      {
        ...lastPart,
        path: AppRoutes.PRODUCTION_HEAD_SEASON_SETTINGS.path + readSeasonSettingsLastView(),
      },
      { name: 'Version', nameKey: 'version', path: lastPart.path },
    ];
  }

  return breadcrumbParts.length > 0
    ? breadcrumbParts
    : [{ name: AppRoutes.HOME.name, nameKey: AppRoutes.HOME.nameKey, path: AppRoutes.HOME.path }];
}

export function useFarmerReportBreadcrumbs(loaderData?: any, persistParams: string[] = []) {
  const { pathname, search } = useLocation();
  const params = useParams();

  // Parse query params into an object
  const urlSearchParams = new URLSearchParams(search);
  const persistedQuery = new URLSearchParams();

  persistParams.forEach((key) => {
    const value = urlSearchParams.get(key);
    if (value) persistedQuery.set(key, value);
  });

  const matchedRoutes = Object.values(AppRoutes).filter((route) =>
    matchPath({ path: route.path, end: false }, pathname)
  );

  // Check if user is Regional Manager
  const isRegionalManager = loaderData?.userMetadata?.organizations
    ?.find((org: any) => org.isCurrent)
    ?.personaRoles?.some(
      (role: any) =>
        role.personaRoleName?.toLowerCase() === PersonaRoleEnum.REGIONAL_MANAGER.toLowerCase()
    );

  // Determine which routes to skip based on geostructure (only for Regional Managers)
  const defaultGeostructureList = loaderData?.defaultGeostructureList;
  const startingAddressPartName = defaultGeostructureList?.[0]?.addressPartName;

  // Define the route hierarchy and where each addressPartName should start
  const getSkipRoutesBasedOnGeostructure = (
    addressPartName: string | undefined
  ): Set<AppRouteKey> => {
    const skipRoutes = new Set<AppRouteKey>();

    // Only apply skip logic for Regional Managers
    if (!addressPartName || !isRegionalManager) return skipRoutes;

    // Based on the routeFarmerReportPageHandler logic:
    switch (addressPartName) {
      case 'region':
      case 'region_part':
        // Start from /farmer-report - no routes to skip
        break;
      case 'settlement':
        // Start from /farmer-report/division-report/undefined
        // Skip FARMER_REPORT root
        skipRoutes.add(AppRouteKey.FARMER_REPORT);
        break;
      case 'settlement_part':
        // Start from /farmer-report/zone-report/undefined
        // Skip FARMER_REPORT and DIVISION_REPORT
        skipRoutes.add(AppRouteKey.FARMER_REPORT);
        skipRoutes.add(AppRouteKey.FARMER_REPORT_DIVISION_REPORT);
        break;
      case 'custom_geography_structure_1':
        // Start from /farmer-report/division-report/undefined/municipality-report/undefined
        // Skip FARMER_REPORT and DIVISION_REPORT
        skipRoutes.add(AppRouteKey.FARMER_REPORT);
        skipRoutes.add(AppRouteKey.FARMER_REPORT_DIVISION_REPORT);
        break;
      default:
        // Default: start from /farmer-report
        break;
    }

    return skipRoutes;
  };

  const skipRoutes = getSkipRoutesBasedOnGeostructure(startingAddressPartName);

  const breadcrumbParts = matchedRoutes
    .filter((route) => !skipRoutes.has(route.key as AppRouteKey))
    .map((route) => {
      const label = route.getName ? route.getName({ params, loaderData }) : route.name;

      // Replace dynamic :params in route path
      let path = route.path;
      Object.entries(params).forEach(([key, val]) => {
        path = path.replace(`:${key}`, String(val));
      });

      // Append selected query params only
      const queryString = persistedQuery.toString();
      const fullPath = queryString ? `${path}?${queryString}` : path;

      const staticLabel = route.staticLabel;
      return { label, path: fullPath, staticLabel };
    });

  return breadcrumbParts.length > 0
    ? breadcrumbParts
    : [{ label: AppRoutes.HOME.name, path: '/', staticLabel: '' }];
}
